<template>
  <div class="rollout mt-4">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div class="d-flex align-items-center" style="gap:0.5rem">
        <h5 class="mb-0">Trinnvis utrulling</h5>
        <button class="rollout-info-btn" :class="{ 'rollout-info-btn--on': helpOpen }"
                @click="helpOpen = !helpOpen" title="Vis/skjul hjelp">
          <font-awesome-icon :icon="['fas', 'info-circle']" />
        </button>
      </div>
      <!-- Says what it reads AND that it sends nothing. Every other button in this panel ships something
           to the fleet, so a read-only action here has to be unmistakable on sight — a bare «Oppdater»,
           or an icon you must hover to understand, is not good enough next to «Slipp til hele flåten». -->
      <button class="bo_btn_secondary rollout-refresh" @click="load" :disabled="loading"
              title="Leser inn på nytt hvilken bølge som er aktiv. Sender ingenting ut til enheter.">
        <font-awesome-icon :icon="['fas', 'sync']" :class="{ 'fa-spin': loading }" />
        {{ loading ? "Henter…" : "Hent status" }}
      </button>
    </div>

    <p class="rollout-intro">
      Slipp en ny versjon til noen få merchants først. Går det bra gjennom noen servicedager,
      slipper du den til resten av flåten. Terminaler hos merchants som ikke er i bølgen blir
      <b>ikke varslet</b> om den nye versjonen.
      <br><span class="rollout-meta">Panelet viser bølgen som er lagret på serveren nå.
      «Hent status» leser den inn på nytt — nyttig hvis noen andre har endret den.</span>
    </p>

    <div v-if="helpOpen" class="rollout-help">
      <b>Hva knappene gjør</b>
      <table class="rollout-help-table">
        <tbody>
        <tr>
          <td><b>Hent status</b></td>
          <td>Leser inn på nytt hvilken bølge som ligger lagret på serveren.
              <b>Sender ingenting ut til enheter.</b></td>
        </tr>
        <tr>
          <td><b>Vis berørte enheter</b></td>
          <td>Lister enhetene til merchantene du har skrevet inn, og hvilken versjon hver av dem
              kjører nå. Endrer ingenting — bruk den for å se omfanget før du starter.</td>
        </tr>
        <tr>
          <td><b>Start testbølge</b></td>
          <td>Merker disse merchantene som testgruppe for den aktive versjonen. Terminaler hos alle
              andre blir ikke varslet om den.</td>
        </tr>
        <tr>
          <td><b>Slipp til hele flåten</b></td>
          <td>Gjør versjonen tilgjengelig for alle merchants. Gjør dette først når testbølgen har
              stått noen servicedager uten problemer.</td>
        </tr>
        <tr>
          <td><b>Avbryt bølge</b></td>
          <td>Stopper utrullingen. Flåten har aldri fått versjonen, så den er trygg — men terminaler
              i bølgen som allerede har oppdatert må få en <b>ny release</b>.</td>
        </tr>
        </tbody>
      </table>
      <div class="mt-2">
        Selve installasjonen skjer ikke her. Bølgen styrer <em>hvem</em> som skal få versjonen — ikke
        <em>når</em>, og den rører ikke managed Play i det hele tatt; den avgjør bare hvilket
        versjonsnummer appen får oppgitt når den spør. En terminal bytter faktisk versjon enten ved at
        Play leverer den av seg selv, ved at noen trykker Oppdater i Play-butikken på enheten, eller
        ved <b>Tving oppdatering</b> i Enheter — som avinstallerer appen, sletter appdata og krever at
        terminalen logges inn på nytt. Bruk den som nødløsning, ikke som utrullingsverktøy.
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label mb-1">App</label>
      <select class="form-control" v-model="packageName" @change="loadDevices">
        <option v-for="p in packages" :key="p" :value="p">{{ p }}</option>
      </select>
    </div>

    <div v-if="current" class="rollout-state mb-3">
      <div>
        <b>Versjon {{ current.targetVersionCode }}</b>
        <span v-if="current.fleetReleased" class="badge bg-success ms-2">Sluppet til hele flåten</span>
        <span v-else class="badge bg-warning text-body ms-2">Testbølge</span>
      </div>
      <div class="rollout-meta">
        Startet {{ current.startedAt || "—" }}
        <span v-if="current.previousVersionCode">
          · resten av flåten står på {{ current.previousVersionCode }}
        </span>
      </div>
    </div>
    <div v-else class="rollout-meta mb-3">Ingen pågående utrulling for denne appen.</div>

    <div class="mb-3">
      <label class="form-label mb-1">Merchants i bølgen</label>
      <textarea class="form-control" rows="3" v-model="merchantsText"
                placeholder="En merchant-slug per linje, f.eks. grefsensushi"
                style="font-family:monospace; font-size:0.82rem"></textarea>
      <div class="rollout-meta mt-1">Bruk merchant-slugen (samme som brukernavnet i Backoffice).</div>
    </div>

    <div class="d-flex gap-2 flex-wrap">
      <button class="bo_btn" @click="startWave"
              :disabled="busy || !packageName || !merchants.length || isLocalDev()"
              :title="isLocalDev() ? LOCAL_DEV_HINT : ''">
        Start testbølge ({{ merchants.length }})
      </button>
      <button class="bo_btn_success" @click="releaseToFleet"
              :disabled="busy || !current || current.fleetReleased || isLocalDev()"
              :title="isLocalDev() ? LOCAL_DEV_HINT : ''">
        Slipp til hele flåten
      </button>
      <button class="bo_btn_secondary" @click="abortWave" :disabled="busy || !current || isLocalDev()"
              :title="isLocalDev() ? LOCAL_DEV_HINT : ''">
        Avbryt bølge
      </button>
      <button class="bo_btn_secondary" @click="loadDevices" :disabled="busy || !merchants.length">
        Vis berørte enheter
      </button>
    </div>

    <div v-if="message" :class="error ? 'text-danger mt-2' : 'text-success mt-2'">{{ message }}</div>

    <div v-if="devices.length" class="mt-3">
      <div class="rollout-meta mb-1">
        {{ devices.length }} enhet(er) i bølgen — versjonen hver av dem kjører nå:
      </div>
      <table class="table table-sm rollout-table">
        <thead>
          <tr><th>Merchant</th><th>Enhet</th><th>Policy</th><th>Versjon</th></tr>
        </thead>
        <tbody>
          <tr v-for="d in devices" :key="d.id">
            <td>{{ d.merchant }}</td>
            <td>{{ d.customName || d.serialNumber }}</td>
            <td>{{ shortPolicy(d.appliedPolicyName) }}</td>
            <td :class="current && d.versionCode === current.targetVersionCode ? 'text-success' : ''">
              {{ d.versionCode > 0 ? d.versionCode : "—" }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <confirm-dialogue ref="confirmDialog"></confirm-dialogue>
  </div>
</template>

<script>
/**
 * Staged rollout of an app release (CAGISTA MDM → Apper).
 *
 * At a few thousand terminals a bad build is every till in the country failing at once, and managed
 * Play cannot serve an older version to roll back — so the only real protection is never exposing the
 * whole fleet to a build that has not run real service days somewhere first.
 *
 * What the wave actually controls: who the in-app "ny versjon" prompt is shown to, and which devices a
 * forced update may target. It does not hard-block a staff member who opens Play Store by hand — see
 * AppRolloutService on why that trade-off is accepted.
 */
import MdmService from "@/services/system/MdmService";
import ConfirmDialogue from "@/components/other/ConfirmDialogue";
import { isLocalDev, refuseIfLocalDev } from "@/util/MdmLocalGuard";

const LOCAL_DEV_HINT = "Skrivebeskyttet fra localhost — bruk produksjons-Backoffice";

export default {
  name: "AppRollout",
  components: { ConfirmDialogue },
  props: {
    // Package names from the Apper list, so the two stay in step.
    packages: { type: Array, default: () => [] }
  },
  data() {
    return {
      LOCAL_DEV_HINT,
      loading: false,
      busy: false,
      helpOpen: false,
      packageName: "",
      rollout: {},
      merchantsText: "",
      devices: [],
      message: "",
      error: false
    };
  },
  computed: {
    current() {
      return this.rollout[this.packageName] || null;
    },
    merchants() {
      return this.merchantsText
        .split("\n")
        .map((m) => m.trim())
        .filter((m) => m.length > 0);
    }
  },
  watch: {
    packages: {
      immediate: true,
      handler(list) {
        if (!this.packageName && list && list.length) {
          this.packageName = list[0];
          this.load();
        }
      }
    },
    current: {
      handler(value) {
        // Show the stored wave when switching app, without clobbering something being typed.
        if (value && !this.merchantsText) {
          this.merchantsText = (value.waveMerchants || []).join("\n");
        }
      }
    }
  },
  methods: {
    isLocalDev,
    // A wave moves real terminals onto the new version; releasing hands it to every merchant. Both run
    // against the shared enterprise, so neither may be started from a developer's browser.
    refuseIfLocalDev() {
      return refuseIfLocalDev(this.$refs.confirmDialog,
        "En utrulling herfra flytter ekte terminaler over på den nye versjonen — og «Slipp til hele " +
        "flåten» gir den til alle merchants. Managed Play kan ikke servere en eldre versjon tilbake.");
    },
    load() {
      this.loading = true;
      MdmService.getRollout().then(
        (res) => {
          this.rollout = (res.data && res.data.packages) || {};
          const c = this.current;
          this.merchantsText = c ? (c.waveMerchants || []).join("\n") : "";
          this.loading = false;
        },
        (error) => { this.fail(error, "Kunne ikke hente utrullingsstatus."); this.loading = false; }
      );
    },
    async startWave() {
      if (await this.refuseIfLocalDev()) return;
      const ok = await this.$refs.confirmDialog.show({
        title: "Start testbølge",
        message:
          `Gi <b>${this.merchants.length}</b> merchant(s) den nye versjonen av <code>${this.packageName}</code> først?<br><br>` +
          "Resten av flåten blir ikke varslet før du slipper den videre.",
        okButton: "Start testbølge", cancelButton: "Avbryt"
      });
      if (!ok) return;
      this.run(MdmService.startRolloutWave(this.packageName, this.merchants), "Testbølge startet.");
    },
    async releaseToFleet() {
      if (await this.refuseIfLocalDev()) return;
      const ok = await this.$refs.confirmDialog.show({
        title: "Slipp til hele flåten",
        message:
          `Gjøre versjon <b>${this.current.targetVersionCode}</b> av <code>${this.packageName}</code> ` +
          "tilgjengelig for <b>alle</b> merchants?<br><br>" +
          "Gjør dette først når testbølgen har stått noen servicedager uten problemer — " +
          "managed Play kan ikke servere en eldre versjon tilbake.",
        okButton: "Slipp til hele flåten", cancelButton: "Avbryt"
      });
      if (!ok) return;
      this.run(MdmService.releaseRolloutToFleet(this.packageName), "Sluppet til hele flåten.");
    },
    async abortWave() {
      if (await this.refuseIfLocalDev()) return;
      const ok = await this.$refs.confirmDialog.show({
        title: "Avbryt bølge",
        message:
          `Stoppe utrullingen av <code>${this.packageName}</code>?<br><br>` +
          "Flåten har aldri fått den, så den er trygg. Terminaler i bølgen som allerede har oppdatert " +
          "må få en <b>ny release</b> — en eldre versjon kan ikke rulles tilbake gjennom Play.",
        okButton: "Avbryt bølge", cancelButton: "Behold"
      });
      if (!ok) return;
      this.run(MdmService.abortRolloutWave(this.packageName), "Bølgen er avbrutt.");
    },
    loadDevices() {
      this.devices = [];
      if (!this.merchants.length || !this.packageName) return;
      this.busy = true;
      MdmService.getRolloutDevices(this.packageName, this.merchants).then(
        (res) => {
          this.devices = (res.data && res.data.devices) || [];
          this.busy = false;
          if (!this.devices.length) {
            this.note("Fant ingen innrullerte enheter for disse merchantene.", true);
          }
        },
        (error) => { this.fail(error, "Kunne ikke hente enheter."); this.busy = false; }
      );
    },
    run(promise, okMessage) {
      this.busy = true;
      promise.then(
        (res) => {
          this.rollout = (res.data && res.data.packages) || {};
          this.note(okMessage, false);
          this.busy = false;
        },
        (error) => { this.fail(error, "Handlingen feilet."); this.busy = false; }
      );
    },
    note(text, isError) {
      this.message = text;
      this.error = !!isError;
    },
    fail(error, fallback) {
      const data = error && error.response && error.response.data;
      this.note((data && data.error) || fallback, true);
    },
    shortPolicy(name) {
      if (!name) return "—";
      const i = name.indexOf("/policies/");
      return i >= 0 ? name.substring(i + "/policies/".length) : name;
    }
  }
};
</script>

<style scoped>
.rollout {
  border-top: 1px solid #e6e6e6;
  padding-top: 1rem;
}
.rollout-info-btn {
  background: none;
  border: none;
  color: #adb5bd;
  cursor: pointer;
  padding: 0 0.2rem;
  line-height: 1;
}
.rollout-info-btn--on {
  color: #ECB109;
}
.rollout-help {
  background: var(--bo-surface-2);
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  padding: 0.75rem;
  font-size: 0.84rem;
  color: var(--bo-ink-2);
  max-width: 60rem;
  margin-bottom: 1rem;
}
.rollout-help-table {
  margin-top: 0.5rem;
}
.rollout-help-table td {
  padding: 0.2rem 0.75rem 0.2rem 0;
  vertical-align: top;
}
.rollout-help-table td:first-child {
  white-space: nowrap;
}
.rollout-refresh {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.rollout-intro {
  font-size: 0.86rem;
  color: var(--bo-muted);
  max-width: 60rem;
}
.rollout-meta {
  font-size: 0.8rem;
  color: var(--bo-muted);
}
.rollout-state {
  background: var(--bo-brand-tint);
  border: 1px solid #ecb109;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
}
.rollout-table {
  font-size: 0.82rem;
  max-width: 60rem;
}
</style>
